import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useStore from "../store.js";
import ModalChatSession from "./Modal/ModalChatSession.jsx";
import ModalGuestHandoffStatus from "./Modal/ModalGuestHandoffStatus.jsx";
import {
  completeGuestChatHandoff,
  consumeGuestHandoffStatus,
  consumeUnavailableGuestChatDraft,
  consumeTransferredGuestChat,
  discardPendingGuestChatHandoff,
  hasPendingGuestChatHandoff,
} from "../utils/guestChatHandoff.js";

const ChatNavigationContext = createContext(null);

export const useChatNavigation = () => {
  const context = useContext(ChatNavigationContext);
  if (!context) {
    throw new Error("useChatNavigation must be used within ChatNavigationProvider");
  }
  return context;
};

const ChatNavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getChatHistory,
    hasActiveChatSession,
    isChatMode,
    resetChat,
    restoreChatHistory,
    setIsChatMode,
    setMobileSidebarOpen,
  } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [handoffNotice, setHandoffNotice] = useState(null);
  const pendingHistoryRef = useRef(null);
  const preservedLocalChatRef = useRef(false);
  const shouldRestoreLocalChatRef = useRef(false);
  const guestHandoffConflictRef = useRef(false);
  const checkedLocationKeyRef = useRef("");
  const handlesNextMainCheckRef = useRef(false);

  const restoreTransferredGuestChat = useCallback((history) => {
    restoreChatHistory(history);
    setIsChatMode(true);
  }, [restoreChatHistory, setIsChatMode]);

  const moveToMain = useCallback((handlesSessionCheck = false, textValue = "") => {
    handlesNextMainCheckRef.current = handlesSessionCheck;
    setMobileSidebarOpen(false);
    navigate("/main", { replace: true, state: { textValue } });
  }, [navigate, setMobileSidebarOpen]);

  const loadActiveHistory = useCallback(async ({ showError = true } = {}) => {
    const history = await getChatHistory();
    if (history?.requestFailed) {
      setErrorMessage("대화 상태를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.");
      if (showError) setIsModalOpen(true);
      return null;
    }
    return history;
  }, [getChatHistory]);

  const openSessionChoice = useCallback((history = null) => {
    pendingHistoryRef.current = history;
    setErrorMessage("");
    setIsModalOpen(true);
  }, []);

  const requestNewSituation = useCallback(async () => {
    shouldRestoreLocalChatRef.current =
      isChatMode || preservedLocalChatRef.current;
    setIsChatMode(false);
    moveToMain(true);

    if (isChatMode || hasActiveChatSession) {
      openSessionChoice();
      return;
    }

    const history = await loadActiveHistory();
    if (!history) return;
    if (history.isChat) {
      openSessionChoice(history);
      return;
    }
    const hasCurrentInput = Array.from(document.querySelectorAll('[data-behavior="question-input"]'))
      .some((input) => typeof input.value === "string" && input.value.trim());
    if (hasCurrentInput) return;
    const draft = consumeUnavailableGuestChatDraft();
    if (draft) moveToMain(true, draft);
  }, [
    hasActiveChatSession,
    isChatMode,
    loadActiveHistory,
    moveToMain,
    openSessionChoice,
    setIsChatMode,
  ]);

  const continueChat = useCallback(async () => {
    let history = pendingHistoryRef.current;
    if (!shouldRestoreLocalChatRef.current && !history) {
      history = await loadActiveHistory();
      if (!history) return;
    }

    if (shouldRestoreLocalChatRef.current) {
      setIsChatMode(true);
    } else if (history?.isChat) {
      restoreChatHistory(history);
    }

    if (guestHandoffConflictRef.current) {
      discardPendingGuestChatHandoff();
      guestHandoffConflictRef.current = false;
    }
    pendingHistoryRef.current = null;
    preservedLocalChatRef.current = false;
    shouldRestoreLocalChatRef.current = false;
    setErrorMessage("");
    setIsModalOpen(false);
  }, [loadActiveHistory, restoreChatHistory, setIsChatMode]);

  const startNewSituation = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    const success = await resetChat();

    if (!success) {
      setErrorMessage("대화를 종료하지 못했습니다. 잠시 후 다시 시도해주세요.");
      setIsLoading(false);
      return;
    }

    if (guestHandoffConflictRef.current && hasPendingGuestChatHandoff()) {
      try {
        const transferred = await completeGuestChatHandoff();
        const transferredGuestChat = transferred
          ? consumeTransferredGuestChat()
          : null;
        if (!transferredGuestChat) {
          setHandoffNotice({
            title: "대화를 이어오지 못했습니다.",
            description: "비로그인 대화가 만료되었습니다. 새로운 대화를 시작해주세요.",
            retryable: false,
          });
        } else {
          const history = await loadActiveHistory({ showError: false });
          if (history?.isChat) {
            restoreTransferredGuestChat(history);
          }
        }
      } catch (error) {
        setHandoffNotice({
          title: "대화를 불러오지 못했습니다.",
          description: "네트워크 상태를 확인한 뒤 다시 시도해주세요.",
          retryable: true,
        });
      }
      guestHandoffConflictRef.current = false;
    }

    pendingHistoryRef.current = null;
    preservedLocalChatRef.current = false;
    shouldRestoreLocalChatRef.current = false;
    setIsLoading(false);
    setIsModalOpen(false);
    const draft = consumeUnavailableGuestChatDraft();
    if (draft) moveToMain(true, draft);
  }, [loadActiveHistory, moveToMain, resetChat, restoreTransferredGuestChat]);

  const leaveChatForHistory = useCallback(() => {
    preservedLocalChatRef.current =
      preservedLocalChatRef.current || isChatMode;
    setIsChatMode(false);
    setMobileSidebarOpen(false);
  }, [isChatMode, setIsChatMode, setMobileSidebarOpen]);

  const retryGuestHandoff = useCallback(async () => {
    setHandoffNotice(null);
    try {
      const transferred = await completeGuestChatHandoff();
      const transferredGuestChat = transferred
        ? consumeTransferredGuestChat()
        : null;
      if (!transferredGuestChat) {
        setHandoffNotice({
          title: "대화를 이어오지 못했습니다.",
          description: "비로그인 대화가 만료되었거나 유효하지 않습니다. 새로운 대화를 시작해주세요.",
          retryable: false,
        });
        return;
      }
      const history = await loadActiveHistory({ showError: false });
      if (!history?.isChat) {
        setHandoffNotice({
          title: "이전 대화를 찾지 못했습니다.",
          description: "대화 이전은 완료됐지만 복원할 내용이 없습니다. 새로운 대화를 시작해주세요.",
          retryable: false,
        });
        return;
      }
      restoreTransferredGuestChat(history);
    } catch (error) {
      setHandoffNotice({
        title: "대화를 불러오지 못했습니다.",
        description: "네트워크 상태를 확인한 뒤 다시 시도해주세요.",
        retryable: true,
      });
    }
  }, [loadActiveHistory, restoreTransferredGuestChat]);

  useEffect(() => {
    if (location.pathname !== "/main") {
      checkedLocationKeyRef.current = "";
      return;
    }

    if (location.pathname === "/main" && handlesNextMainCheckRef.current) {
      handlesNextMainCheckRef.current = false;
      checkedLocationKeyRef.current = location.key;
      return;
    }

    if (
      isChatMode ||
      checkedLocationKeyRef.current === location.key
    ) {
      return;
    }

    checkedLocationKeyRef.current = location.key;
    let cancelled = false;
    const checkMainChat = async () => {
      let transferredGuestChat = consumeTransferredGuestChat();

      if (!transferredGuestChat && hasPendingGuestChatHandoff()) {
        try {
          const transferred = await completeGuestChatHandoff();
          if (transferred) {
            transferredGuestChat = consumeTransferredGuestChat();
          }
        } catch (error) {
          if (!cancelled) {
            setHandoffNotice({
              title: "대화를 불러오지 못했습니다.",
              description: "네트워크 상태를 확인한 뒤 다시 시도해주세요.",
              retryable: true,
            });
          }
          return;
        }
      }

      const handoffStatus = consumeGuestHandoffStatus();
      const history = await loadActiveHistory({ showError: false });
      if (cancelled) return;

      if (transferredGuestChat) {
        if (history?.isChat) {
          restoreTransferredGuestChat(history);
        } else {
          setHandoffNotice({
            title: "이전 대화를 찾지 못했습니다.",
            description: "대화 이전은 완료됐지만 복원할 내용이 없습니다. 새로운 대화를 시작해주세요.",
            retryable: false,
          });
        }
        return;
      }

      if (handoffStatus === "user_session_exists") {
        guestHandoffConflictRef.current = true;
        pendingHistoryRef.current = history?.isChat ? history : null;
        setErrorMessage(
          "로그인 계정에 진행 중인 대화가 있습니다. 기존 대화를 계속하거나 종료한 뒤 비로그인 대화를 이어갈 수 있습니다.",
        );
        setIsModalOpen(true);
        return;
      }

      const hasCurrentInput = Array.from(document.querySelectorAll('[data-behavior="question-input"]'))
        .some((input) => typeof input.value === "string" && input.value.trim());
      const draft = history && !history.isChat && !location.state?.textValue && !hasCurrentInput
        ? consumeUnavailableGuestChatDraft()
        : "";
      if (handoffStatus && handoffStatus !== "server_error") {
        setHandoffNotice({
          title: "대화를 이어오지 못했습니다.",
          description: draft
            ? "이전 대화는 이어오지 못했습니다. 작성한 내용은 전송하지 않은 초안으로 입력창에 남겼습니다."
            : "비로그인 대화가 만료되었거나 유효하지 않습니다. 새로운 대화를 시작해주세요.",
          retryable: false,
        });
      }

      if (draft) moveToMain(true, draft);

      if (history?.isChat) {
        openSessionChoice(history);
      }
    };

    checkMainChat();

    return () => {
      cancelled = true;
    };
  }, [
    isChatMode,
    loadActiveHistory,
    location.key,
    location.pathname,
    location.state,
    moveToMain,
    openSessionChoice,
    restoreTransferredGuestChat,
  ]);

  const value = useMemo(
    () => ({
      leaveChatForHistory,
      requestNewSituation,
    }),
    [leaveChatForHistory, requestNewSituation],
  );

  return (
    <ChatNavigationContext.Provider value={value}>
      {children}
      <ModalChatSession
        isOpen={isModalOpen}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onContinue={continueChat}
        onStartNew={startNewSituation}
        title={
          guestHandoffConflictRef.current
            ? "로그인 계정에 진행 중인 대화가 있어요"
            : undefined
        }
        description={
          guestHandoffConflictRef.current
            ? "기존 대화를 계속하거나, 기존 대화를 종료하고 방금 작성한 비로그인 대화를 이어갈 수 있어요."
            : undefined
        }
        continueLabel={
          guestHandoffConflictRef.current ? "기존 대화 계속" : undefined
        }
        startNewLabel={
          guestHandoffConflictRef.current
            ? "비로그인 대화 이어가기"
            : undefined
        }
      />
      <ModalGuestHandoffStatus
        notice={handoffNotice}
        onClose={() => setHandoffNotice(null)}
        onRetry={retryGuestHandoff}
      />
    </ChatNavigationContext.Provider>
  );
};

export default ChatNavigationProvider;
